import React, { useCallback, useEffect, useMemo, useState } from "react";
import { FiPlus } from "react-icons/fi";
import "./Quality.css";
import { showErrorToast, showSuccessToast } from "../../constants/ToastService";
import { qualityApi } from "./qualityApi";
import { MockBanner, ResultPill, hasPerm } from "./qualityUi";
import TestCaseForm from "./TestCaseForm";

/**
 * The "Test Cases" tab in the Sprint ticket workspace.
 *
 * QA links the test cases that prove a ticket works. They live in the global
 * library, so every later regression run for the ticket's module picks them
 * up, and a release snapshot records which cases covered the ticket when it
 * shipped. Reports its count through `onCount` for the tab badge.
 */
export default function TicketTestCasesTab({ ticketId, ticketCode, moduleName, onCount }) {
    const [linked, setLinked] = useState([]);
    const [library, setLibrary] = useState([]);
    const [pick, setPick] = useState("");
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [writing, setWriting] = useState(false);
    const canEdit = hasPerm("Manage Test Cases");

    const show = useCallback((list) => {
        setLinked(list || []);
        onCount && onCount((list || []).length);
    }, [onCount]);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [mine, all] = await Promise.all([
                qualityApi.getTicketTestCases(ticketId),
                qualityApi.getTestCases({ status: "Active" }),
            ]);
            show(mine.data);
            setLibrary(all.data || []);
        } catch (e) {
            showErrorToast(e.message || "Could not load test cases.");
        } finally {
            setLoading(false);
        }
    }, [ticketId, show]);

    useEffect(() => { load(); }, [load]);

    // Cases from the ticket's own module first, already-linked ones left out.
    const options = useMemo(() => {
        const have = new Set(linked.map((c) => c.code));
        return library
            .filter((c) => !have.has(c.code))
            .sort((a, b) => (a.module === moduleName ? 0 : 1) - (b.module === moduleName ? 0 : 1) || a.code.localeCompare(b.code));
    }, [library, linked, moduleName]);

    const link = async () => {
        if (!pick) { showErrorToast("Choose a test case first."); return; }
        setBusy(true);
        try {
            const res = await qualityApi.linkTicketTestCase(ticketId, pick);
            show(res.data);
            setPick("");
            showSuccessToast(res.message || `${pick} linked to ${ticketCode}.`);
        } catch (e) {
            showErrorToast(e.message || "Could not link the test case.");
        } finally {
            setBusy(false);
        }
    };

    const unlink = async (code) => {
        setBusy(true);
        try {
            const res = await qualityApi.unlinkTicketTestCase(ticketId, code);
            show(res.data);
        } catch (e) {
            showErrorToast(e.message || "Could not unlink the test case.");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="qa-page qa-page--embed">
            <MockBanner />
            <div className="qa-banner qa-banner--info">
                <span className="qa-banner__tx">
                    Link the test cases that prove this ticket works. They join the global library, and every later
                    regression run{moduleName ? <> for <b>{moduleName}</b></> : ""} includes them.
                </span>
            </div>

            <div className="qa-card qa-card--solo">
                <table className="qa-tbl">
                    <thead>
                        <tr><th>Test case</th><th>Title</th><th>Type</th><th>Regression</th><th>Last result</th><th /></tr>
                    </thead>
                    <tbody>
                        {loading && <tr><td colSpan={6} className="qa-empty">Loading…</td></tr>}
                        {!loading && linked.length === 0 && (
                            <tr>
                                <td colSpan={6} className="qa-empty">
                                    No test cases yet. This ticket can't be checked in regression until one is linked.
                                </td>
                            </tr>
                        )}
                        {linked.map((c) => (
                            <tr key={c.code}>
                                <td className="qa-code qa-mono">{c.code}</td>
                                <td>
                                    {c.title}
                                    <div className="qa-hint">{c.module} · {c.sub_module}</div>
                                </td>
                                <td><span className="qa-chip">{c.case_type}</span></td>
                                <td className="qa-nw">
                                    {c.in_regression ? <span className="qa-chip qa-chip--blue">Yes</span> : <span className="qa-faint">No</span>}
                                    {c.critical && <> <span className="qa-chip qa-chip--red">Critical</span></>}
                                </td>
                                <td className="qa-nw">
                                    {c.last_result
                                        ? <><ResultPill value={c.last_result.result} /> <span className="qa-hint">{c.last_result.version}</span></>
                                        : <span className="qa-pill qa-pill--warn">Never run</span>}
                                </td>
                                <td>
                                    {canEdit && (
                                        <button className="qa-btn qa-btn--sm qa-btn--danger" disabled={busy} onClick={() => unlink(c.code)}>
                                            Unlink
                                        </button>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {canEdit && (
                <div className="qa-form" style={{ marginTop: 14, alignItems: "end" }}>
                    <label className="qa-fl">
                        Link an existing test case
                        <select id={`qa-link-${ticketId}`} className="qa-input" value={pick} onChange={(e) => setPick(e.target.value)}>
                            <option value="">Choose from the library…</option>
                            {options.map((c) => (
                                <option key={c.code} value={c.code}>{c.code} · {c.title} ({c.module})</option>
                            ))}
                        </select>
                    </label>
                    <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
                        <button className="qa-btn" disabled={busy || !pick} onClick={link}>Link</button>
                        <button className="qa-btn qa-btn--primary" onClick={() => setWriting(true)}>
                            <FiPlus size={12} /> Write a new one
                        </button>
                    </div>
                </div>
            )}

            {writing && (
                <TestCaseForm
                    prefill={moduleName ? { module: moduleName } : undefined}
                    context={{ ticketId, ticketCode }}
                    onClose={() => setWriting(false)}
                    onSaved={() => { setWriting(false); load(); }}
                />
            )}
        </div>
    );
}
